import { useCallback, useEffect, useRef, useState } from 'react';
import type Hls from 'hls.js';
import { watchForBlackVideo } from '../videoCheck';
import { ApiError, iptv, type EpgProgramme, type IptvChannel } from '../api';
import { channelDisplay, ChannelLogo, clock, LiveBadge, playLink } from '../components/LiveBits';
import { goBack, navigate } from '../router';
import { addToMultiview } from './Multiview';
import { PlayerClock } from '../components/PlayerOsd';
import { BadgeRow } from '../badges';
import { FixedScore, ScoreChip, SKIN_LABEL, SportPanel, SportStrip, SportTicker, useChannelMatch } from '../components/SportPanel';

/** Beeldlabels uit de zendernaam (4K, FHD, HEVC), in dezelfde badges als de andere spelers. */
function liveFlags(raw: string): string[] {
  const t = raw.toUpperCase();
  const out: string[] = [];
  if (/\b(4K|UHD|2160P?)\b/.test(t)) out.push('4K');
  else if (/\b(FHD|1080P?)\b/.test(t)) out.push('1080p');
  if (/\bHDR\b/.test(t)) out.push('HDR');
  if (/\b(HEVC|H\.?265)\b/.test(t)) out.push('HEVC');
  return out;
}

/**
 * Live-player voor IPTV. De server verpakt de zender als HLS (zonder het beeld te hercoderen).
 * Op/neer: zappen binnen dezelfde lijst (favorieten, anders dezelfde categorie).
 */
/** Vorige zender, bewaard zolang de app open is */
const zapHistory: { current: string | null; previous: string | null } = { current: null, previous: null };

export function LivePlayer({ accountId, streamId, start, end }: { accountId: string; streamId: number; start?: number; end?: number }) {
  const catchup = start ? { start, end: end ?? Date.now() } : undefined;
  const video = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const hideTimer = useRef<number | undefined>(undefined);
  const [channel, setChannel] = useState<IptvChannel | null>(null);
  const [zapList, setZapList] = useState<IptvChannel[]>([]);
  const [allChannels, setAllChannels] = useState<IptvChannel[]>([]);
  const [digits, setDigits] = useState('');
  const digitTimer = useRef<number | undefined>(undefined);
  const [programmes, setProgrammes] = useState<EpgProgramme[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [buffering, setBuffering] = useState(true);
  const [controls, setControls] = useState(true);
  const [attempt, setAttempt] = useState(0);
  // Reservebron (ander IPTV-account) en automatisch opnieuw proberen bij wegvallen
  const [via, setVia] = useState<string | null>(null);
  const autoRetries = useRef(0);
  // Geen beeld (bijv. 4K/HEVC die deze browser niet kan): de server zet het om
  const [transcode, setTranscode] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [playing, setPlaying] = useState(true);
  // Speelt deze zender een wedstrijd? Dan de stand in de infobalk en het wedstrijdcentrum
  const sport = useChannelMatch(accountId, streamId, !!start);
  const sportDetail = sport.detail;
  const showStrip = sport.parts.strip && !!sportDetail;
  const showTicker = sport.parts.ticker && !!sportDetail && sport.ticker.length > 0;
  const full = showStrip || showTicker || (sport.parts.panel && !!sportDetail);
  const [panel, setPanel] = useState(false);
  // Volledige weergave: het wedstrijdcentrum gaat vanzelf open (op een breed scherm)
  const opened = useRef<string | null>(null);
  useEffect(() => {
    const id = sportDetail?.match.id ?? null;
    if (!id || opened.current === id) return;
    opened.current = id;
    if (sport.parts.panel && window.innerWidth >= 1000) setPanel(true);
  }, [sportDetail?.match.id, sport.parts.panel]);
  const togglePlay = () => {
    const el = video.current;
    if (!el) return;
    if (el.paused) void el.play().catch(() => undefined);
    else el.pause();
  };

  // Vorige zender onthouden (alleen live, niet bij terugkijken)
  useEffect(() => {
    if (start) return;
    const key = `${accountId}:${streamId}`;
    if (zapHistory.current && zapHistory.current !== key) zapHistory.previous = zapHistory.current;
    zapHistory.current = key;
  }, [accountId, streamId, start]);
  const previous = zapHistory.previous && zapHistory.previous !== `${accountId}:${streamId}` ? zapHistory.previous : null;
  const previousName = previous ? allChannels.find((c) => `${c.accountId}:${c.streamId}` === previous)?.name : undefined;
  const goPrevious = () => {
    if (!previous) return;
    const [acc, id] = previous.split(':');
    navigate(playLink(acc, Number(id)), { replace: true });
  };

  // Zendernummer intoetsen: positie in de zenderlijst (zoals in de gids), na 1,5 s schakelen
  const typedChannel = digits ? allChannels[Number(digits) - 1] : undefined;
  useEffect(() => {
    if (!digits) return;
    window.clearTimeout(digitTimer.current);
    digitTimer.current = window.setTimeout(() => {
      const ch = allChannels[Number(digits) - 1];
      setDigits('');
      if (ch) navigate(playLink(ch.accountId, ch.streamId), { replace: true });
    }, 1500);
    return () => window.clearTimeout(digitTimer.current);
  }, [digits, allChannels]);

  // Kanaalgegevens en zaplijst
  useEffect(() => {
    iptv.channels().then((r) => {
      const ch = r.channels.find((c) => c.accountId === accountId && c.streamId === streamId) ?? null;
      setChannel(ch);
      setAllChannels(r.channels);
      if (!ch) return;
      const favs = r.channels.filter((c) => c.favorite);
      setZapList(ch.favorite && favs.length > 1 ? favs : r.channels.filter((c) => c.accountId === ch.accountId && c.categoryId === ch.categoryId));
    }).catch(() => undefined);
  }, [accountId, streamId]);

  // Wat er nu en straks is
  useEffect(() => {
    const load = () => {
      const from = Date.now() - 3 * 3600_000;
      iptv.epg(accountId, [streamId], from, Date.now() + 6 * 3600_000)
        .then((r) => setProgrammes(r.programmes[String(streamId)] ?? []))
        .catch(() => undefined);
    };
    load();
    const t = window.setInterval(() => {
      setNow(Date.now());
      load();
    }, 60_000);
    return () => window.clearInterval(t);
  }, [accountId, streamId]);

  // Stream starten
  useEffect(() => {
    const el = video.current;
    if (!el) return;
    let cancelled = false;
    let sid: string | null = null;
    setError(null);
    setBuffering(true);
    const unwatch = watchForBlackVideo(el, () => {
      if (!cancelled && !transcode) setTranscode(true);
    });

    iptv.play(accountId, streamId, { ...(catchup ? { start: catchup.start, end: catchup.end } : {}), fmp4: true, transcode })
      .then(async ({ sessionId, url, fallback }) => {
        sid = sessionId;
        setVia(fallback ? fallback.accountName : null);
        if (cancelled) {
          void iptv.stop(sessionId);
          return;
        }
        if (el.canPlayType('application/vnd.apple.mpegurl')) {
          el.src = url;
          el.play().catch(() => undefined);
          return;
        }
        const { default: HlsLib } = await import('hls.js');
        if (cancelled) return;
        const hls = new HlsLib({
          // Terugkijken: vanaf het begin; live: vlak achter de rand
          startPosition: catchup ? 0 : -1,
          liveSyncDurationCount: 3,
          maxBufferLength: 30,
          manifestLoadingMaxRetry: 6,
          levelLoadingMaxRetry: 6,
          fragLoadingMaxRetry: 6,
        });
        hlsRef.current = hls;
        let recovered = false;
        hls.on(HlsLib.Events.ERROR, (_e, d) => {
          if (!d.fatal) return;
          if (d.type === HlsLib.ErrorTypes.MEDIA_ERROR && !recovered) {
            recovered = true;
            hls.recoverMediaError();
            return;
          }
          // Weggevallen (live): twee keer vanzelf opnieuw; de server kiest dan zo nodig je andere account
          if (d.type !== HlsLib.ErrorTypes.MEDIA_ERROR && !catchup && autoRetries.current < 2) {
            autoRetries.current += 1;
            setBuffering(true);
            setAttempt((n) => n + 1);
            return;
          }
          setError(
            d.type === HlsLib.ErrorTypes.MEDIA_ERROR
              ? 'De browser kan het beeld van deze zender niet decoderen (mogelijk HEVC).'
              : 'De zender is weggevallen.',
          );
        });
        hls.on(HlsLib.Events.MANIFEST_PARSED, () => {
          el.play().catch(() => undefined);
        });
        hls.loadSource(url);
        hls.attachMedia(el);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.code === 'stream_limit') setError(err.message || 'Maximaal aantal streams bereikt. Stop eerst een andere stream.');
        else setError(err instanceof ApiError && err.message !== err.code ? err.message : 'Deze zender kon niet gestart worden.');
        setBuffering(false);
      });

    const onHide = () => {
      if (sid) void iptv.stop(sid);
    };
    window.addEventListener('pagehide', onHide);

    return () => {
      cancelled = true;
      unwatch();
      window.removeEventListener('pagehide', onHide);
      hlsRef.current?.destroy();
      hlsRef.current = null;
      el.removeAttribute('src');
      el.load();
      if (sid) void iptv.stop(sid);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, streamId, attempt, start, end, transcode]);

  useEffect(() => {
    autoRetries.current = 0;
  }, [accountId, streamId]);
  useEffect(() => {
    if (!via) return;
    const t = window.setTimeout(() => setVia(null), 7000);
    return () => window.clearTimeout(t);
  }, [via]);

  const poke = useCallback(() => {
    setControls(true);
    window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => setControls(false), 4000);
  }, []);

  useEffect(() => {
    poke();
    return () => window.clearTimeout(hideTimer.current);
  }, [poke, streamId]);

  const zap = useCallback((dir: 1 | -1) => {
    if (zapList.length < 2) return;
    const i = zapList.findIndex((c) => c.accountId === accountId && c.streamId === streamId);
    const next = zapList[(i + dir + zapList.length) % zapList.length];
    navigate(playLink(next.accountId, next.streamId), { replace: true });
  }, [zapList, accountId, streamId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Backspace') {
        e.preventDefault();
        e.stopPropagation();
        if (document.fullscreenElement) void document.exitFullscreen();
        goBack();
        return;
      }
      if (catchup && !controls && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
        e.preventDefault();
        e.stopPropagation();
        const el = video.current;
        if (el) el.currentTime = Math.max(0, el.currentTime + (e.key === 'ArrowLeft' ? -10 : 30));
        poke();
        return;
      }
      if (!catchup && /^[0-9]$/.test(e.key) && !(e.target instanceof HTMLInputElement)) {
        e.preventDefault();
        e.stopPropagation();
        setDigits((d) => (d + e.key).slice(-4).replace(/^0+/, ''));
        return;
      }
      if (!catchup && (e.key === 'p' || e.key === 'P' || e.key === 'Last')) {
        e.preventDefault();
        goPrevious();
        return;
      }
      if (!catchup && !controls && (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ChannelUp' || e.key === 'ChannelDown')) {
        e.preventDefault();
        e.stopPropagation();
        zap(e.key === 'ArrowUp' || e.key === 'ChannelUp' ? -1 : 1);
        return;
      }
      if (!controls) {
        e.stopPropagation();
        e.preventDefault();
      }
      poke();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controls, poke, zap, start, previous]);

  const at = catchup ? catchup.start + 1000 : now;
  const current = programmes.find((p) => p.start <= at && p.stop > at);
  const next = programmes.find((p) => p.start >= (current?.stop ?? now));
  const progress = current ? (now - current.start) / (current.stop - current.start) : undefined;
  const name = channel ? channelDisplay(channel.name).name : 'Live TV';

  return (
    <div className={`player ${controls ? 'show-controls' : ''}${full ? ' sp-full' : ''}${full && panel ? ' sp-with-panel' : ''}${showStrip ? ' sp-has-strip' : ''}${showTicker ? ' sp-has-ticker' : ''}`} onMouseMove={poke} onClick={poke}>
      <video
        ref={video}
        className="player-video"
        playsInline
        onWaiting={() => setBuffering(true)}
        onPlaying={() => setBuffering(false)}
        onCanPlay={() => setBuffering(false)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />
      {buffering && !error && <div className="player-spinner" role="status" aria-label="Zender laden" />}
      {panel && sportDetail && <SportPanel d={sportDetail} onClose={() => setPanel(false)} />}
      {sportDetail && sport.parts.score && !controls && <FixedScore d={sportDetail} />}
      {(showStrip || showTicker) && sportDetail && (
        <div className="sp-bottom">
          {showStrip && <SportStrip d={sportDetail} />}
          {showTicker && <SportTicker blocks={sport.ticker} />}
        </div>
      )}
      {via && !error && <div className="player-resume" role="status"><span>Via je andere IPTV-account ({via})</span></div>}

      {error && (
        <div className="player-error" role="alert">
          <p>{error}</p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" data-autofocus onClick={() => setAttempt((n) => n + 1)}>Opnieuw proberen</button>
            <button className="btn" onClick={goBack}>Terug</button>
          </div>
        </div>
      )}

      <div className="player-top">
        <button className="icon-btn" onClick={goBack} aria-label="Terug">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
        </button>
        <ChannelLogo src={channel?.logo ?? null} name={name} size={44} />
        <div className="player-title">
          <strong>{name}</strong>
          <span className="muted">{channel?.categoryName}</span>
        </div>
        <span style={{ marginLeft: 'auto' }}>
          {transcode && <span className="mode-badge">Omgezet door de server</span>}{' '}
          {catchup ? <span className="mode-badge">Terugkijken vanaf {clock(catchup.start)}</span> : <LiveBadge />}
        </span>
        {sportDetail && <ScoreChip d={sportDetail} />}
        <PlayerClock remaining={!catchup && current ? Math.max(0, (current.stop - now) / 1000) : null} />
      </div>

      <div className="player-bottom">
        {digits && (
          <div className="zap-digits" role="status" aria-live="assertive">
            <strong>{digits}</strong>
            <span>{typedChannel ? typedChannel.name : 'Geen zender met dit nummer'}</span>
          </div>
        )}
        <div className="player-osd live-osd" aria-label="Wat er nu op is">
          <h2 className="player-osd-title">{current ? current.title : name}</h2>
          <div className="player-osd-line">{current ? `${clock(current.start)}–${clock(current.stop)} · ${name}` : channel?.categoryName ?? ''}</div>
          {current?.desc && <div className="player-osd-desc clamp-2">{current.desc}</div>}
          <div className="player-osd-meta">
            {next && <span className="osd-next">Straks {clock(next.start)} · {next.title}</span>}
          </div>
          {channel && liveFlags(channel.name).length > 0 && <BadgeRow labels={liveFlags(channel.name)} place="player" size="lg" />}
        </div>
        <div className="scrub">
          {current && progress !== undefined ? (
            <>
              <span className="time">{clock(current.start)}</span>
              <div className="live-progress" aria-label={`${Math.round(progress * 100)}% van het programma`}><div style={{ width: `${Math.min(100, Math.round(progress * 100))}%` }} /></div>
              <span className="time">{clock(current.stop)}</span>
            </>
          ) : <span className="live-pill">{catchup ? 'TERUGKIJKEN' : 'LIVE'}</span>}
        </div>
        <div className="player-buttons" data-focus-group>
          <button className="icon-btn icon-btn-lg" onClick={togglePlay} aria-label={playing ? 'Pauzeren' : 'Afspelen'}>
            {playing ? (
              <svg width="30" height="30" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6" y="4" width="4" height="16" rx="1" /><rect x="14" y="4" width="4" height="16" rx="1" /></svg>
            ) : (
              <svg width="30" height="30" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 4l13 8-13 8z" /></svg>
            )}
          </button>
          {sportDetail && <button className="chip" aria-pressed={panel} onClick={() => setPanel((v) => !v)}>Wedstrijdcentrum</button>}
          {sport.found && <button className="chip" onClick={() => { const v = sport.next(); sport.setSkin(v); if (v === 'off' || v === 'score') setPanel(false); }} title="Uit, alleen de stand, volledig of je eigen samenstelling">{SKIN_LABEL[sport.skin]}</button>}
          {zapList.length > 1 && (
            <>
              <button className="chip" onClick={() => zap(-1)} aria-label="Vorige zender">▲ Vorige</button>
              <button className="chip" onClick={() => zap(1)} aria-label="Volgende zender">▼ Volgende</button>
            </>
          )}
          {!catchup && previous && (
            <button className="chip" onClick={goPrevious} aria-label={`Terug naar vorige zender${previousName ? `: ${previousName}` : ''}`}>↩ {previousName ? previousName.replace(/^\s*[|[(]?\s*[A-Z]{2,3}\s*[|\]):-]\s*/i, '').slice(0, 18) : 'Vorige zender'}</button>
          )}
          {!catchup && channel?.catchup && current && (
            <button className="chip" onClick={() => navigate(playLink(accountId, streamId, { start: current.start, end: current.stop }), { replace: true })}>⟲ Vanaf begin</button>
          )}
          {catchup && <button className="chip" onClick={() => navigate(playLink(accountId, streamId), { replace: true })}>Naar live</button>}
          {!catchup && (
            <button className="chip" onClick={() => { addToMultiview(accountId, streamId); navigate('/live/multi'); }}>Multiview</button>
          )}
          <div className="spacer" />
          <button className="icon-btn" data-autofocus onClick={() => { if (document.fullscreenElement) void document.exitFullscreen(); else void document.documentElement.requestFullscreen().catch(() => undefined); }} aria-label="Volledig scherm">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" /></svg>
          </button>
        </div>
      </div>
    </div>
  );
}
